import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Phone, MessageCircle } from "lucide-react";
import {
  BRAND_NAME,
  BRAND_PHONE,
  BRAND_PHONE_DISPLAY,
  BRAND_WHATSAPP,
  BRAND_WHATSAPP_DISPLAY,
  BRAND_TAGLINE_IT,
  BRAND_TAGLINE_EN,
  BRAND_LEGAL_NAME,
  BRAND_LEGAL_ADDRESS,
  BRAND_FISCAL_CODE,
  BRAND_VAT,
  BRAND_PEC,
  BRAND_REA,
  BRAND_EMAIL,
  BRAND_ODR_URL,
} from "@/lib/brand";

/**
 * Minimal site footer — legal & bottom bar only.
 *
 * "How to reach us" + map + Google reviews have been promoted to a dedicated
 * customer homepage (`/home`) so they are surfaced clearly on first landing
 * instead of buried at the bottom of every page.
 */
export default function SiteFooter() {
  const { i18n } = useTranslation();
  const isEN = i18n.language === "en";
  const waNum = BRAND_WHATSAPP.replace(/[^0-9]/g, "");

  return (
    <footer className="mt-12 border-t border-white/5 bg-zinc-950" data-testid="site-footer">
      {/* Legal / company details */}
      <div className="border-t border-white/5 bg-zinc-950/60">
        <div className="max-w-5xl mx-auto px-5 py-5 text-[11px] text-zinc-500 leading-relaxed" data-testid="footer-legal">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
            <div className="space-y-0.5">
              <p className="text-zinc-300 font-semibold tracking-wide">{BRAND_LEGAL_NAME}</p>
              <p>{BRAND_LEGAL_ADDRESS}</p>
              <p>
                <span className="text-zinc-600">{isEN ? "Tax code" : "Codice Fiscale"}:</span>{" "}
                <span className="font-mono text-zinc-300">{BRAND_FISCAL_CODE}</span>
                <span className="mx-2 text-zinc-700">·</span>
                <span className="text-zinc-600">{isEN ? "VAT" : "Partita IVA"}:</span>{" "}
                <span className="font-mono text-zinc-300">{BRAND_VAT}</span>
                <span className="mx-2 text-zinc-700">·</span>
                <span className="text-zinc-600">REA:</span>{" "}
                <span className="font-mono text-zinc-300">{BRAND_REA}</span>
              </p>
              <p>
                <span className="text-zinc-600">PEC:</span>{" "}
                <a href={`mailto:${BRAND_PEC}`} className="font-mono text-zinc-300 hover:text-[#FFD700] transition">{BRAND_PEC}</a>
                <span className="mx-2 text-zinc-700">·</span>
                <span className="text-zinc-600">{isEN ? "Email" : "Email"}:</span>{" "}
                <a href={`mailto:${BRAND_EMAIL}`} className="font-mono text-zinc-300 hover:text-[#FFD700] transition">{BRAND_EMAIL}</a>
              </p>
              <p className="text-zinc-600 mt-1">
                {isEN ? "EU dispute resolution: " : "Risoluzione controversie UE: "}
                <a href={BRAND_ODR_URL} target="_blank" rel="noopener noreferrer"
                   className="text-zinc-400 underline hover:text-[#FFD700] transition"
                   data-testid="footer-odr-link">
                  {isEN ? "ODR platform" : "piattaforma ODR"}
                </a>
                <span className="text-zinc-700"> · Reg. UE 524/2013</span>
              </p>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              <Link to="/terms" className="text-zinc-400 hover:text-[#FFD700] transition" data-testid="footer-link-terms">
                {isEN ? "Rental Terms & Conditions" : "Condizioni di Noleggio"}
              </Link>
              <Link to="/privacy-policy" className="text-zinc-400 hover:text-[#FFD700] transition" data-testid="footer-link-privacy">
                {isEN ? "Privacy & Cookie Policy" : "Privacy e Cookie Policy"}
              </Link>
              <button
                type="button"
                onClick={() => {
                  try { localStorage.removeItem("sr_cookies_v1"); } catch {}
                  // Notify the (already mounted) banner to re-evaluate its visibility
                  window.dispatchEvent(new CustomEvent("cookies:reset"));
                }}
                className="text-zinc-400 hover:text-[#FFD700] transition underline-offset-2 hover:underline"
                data-testid="footer-cookie-prefs">
                {isEN ? "Manage cookie preferences" : "Gestisci preferenze cookie"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/5">
        <div className="max-w-5xl mx-auto px-5 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-zinc-500">
          <p>© {new Date().getFullYear()} {BRAND_NAME} · {isEN ? BRAND_TAGLINE_EN : BRAND_TAGLINE_IT}</p>
          <div className="flex items-center gap-2">
            <a href={`tel:${BRAND_PHONE}`} className="inline-flex items-center gap-1.5 hover:text-zinc-200 transition" data-testid="footer-phone">
              <Phone className="w-3.5 h-3.5" />
              {BRAND_PHONE_DISPLAY}
            </a>
            <span className="text-zinc-700">·</span>
            <a href={`https://wa.me/${waNum}`} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1.5 text-[#25D366] hover:brightness-110 transition" data-testid="footer-whatsapp">
              <MessageCircle className="w-3.5 h-3.5" />
              {BRAND_WHATSAPP_DISPLAY}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
